import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Turn a date-only string ("2026-01-01", or "2026-01-01T..." with any time suffix)
// into a Date anchored to LOCAL midnight. Returns null if it isn't a date.
//
// Why this exists: a transaction date is a calendar day — what the bank reported —
// not an instant. It has no time and no timezone. But `new Date("2026-01-01")`
// turns it into an instant: JavaScript parses a date-only string as UTC midnight,
// which then renders as the PREVIOUS day for anyone west of UTC (in Chile, 30/06
// shows as 29/06). Anchoring to local midnight makes the parse and the render use
// the same timezone, so they cancel out and the day survives in every timezone.
//
// Use this anywhere a stored day is displayed. Never `new Date(txn.date)`.
//
// El año va con `setFullYear` y no en `new Date(y, m - 1, d)`, porque ese constructor lee
// los años 0 a 99 como 1900 a 1999: una fecha mal tecleada en una importación
// ("0024-05-10") se mostraba como 10/5/1924, un año verosímil que esconde el error.
// Con `setFullYear` sale el año 24 tal cual, que se nota. Año, mes y día van en la misma
// llamada: si el año se fijara aparte, un 29 de febrero armado primero sobre un año que no
// es bisiesto ya habría corrido al 1 de marzo. Para los años desde el 100 el resultado es
// el mismo instante que daba el constructor.
export function parseDateOnly(dateStr) {
  if (!dateStr) return null;
  const [y, m, d] = String(dateStr).split("T")[0].split("-").map(Number);
  if (!y || !m || !d) return null;
  const fecha = new Date(2000, 0, 1);
  fecha.setFullYear(y, m - 1, d);
  return fecha;
}

// Format a date-only string using the given locale, without timezone shifting.
export function formatDateOnly(dateStr, locale, options) {
  if (!dateStr) return "";
  const fecha = parseDateOnly(dateStr);
  if (!fecha) return String(dateStr);
  return fecha.toLocaleDateString(locale, options);
}

// Format an INSTANT (a stored timestamp: when something happened) in 24-hour time.
//
// The opposite case to `formatDateOnly`, and the distinction matters:
//   - a transaction date is a calendar DAY, so converting timezones is the bug;
//   - "last synced at" is an INSTANT, so converting to the reader's timezone is right.
//
// `hour12: false` is the whole point here. Without it, `toLocaleString()` follows the
// browser locale and Chilean Spanish renders "5:21:19 p. m." — a 12-hour clock in an
// app whose every other time field (including the `datetime-local` inputs right next
// to this one) is 24-hour. Pass the i18n language so the date part stays localised.
export function formatDateTime(value, locale) {
  if (!value) return "";
  const fecha = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(fecha.getTime())) return String(value);
  return fecha.toLocaleString(locale, { hour12: false });
}

// Parse a number string using the tenant's regional config (deterministic).
// Pass `{ thousand_separator, decimal_separator }` from `tenant.config`.
// Examples (with thousand="." decimal=","): "1.234,56" -> 1234.56
// Examples (with thousand="," decimal="."): "1,234.56" -> 1234.56
// Falls back to a heuristic when config is missing — handles the edge case of
// pasting from a source that uses a different convention than the tenant.
export function parseNumberByConfig(value, config) {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return value;
  const s = String(value).trim().replace(/\s+/g, "");
  if (!s) return 0;

  const thousand = config?.thousand_separator;
  const decimal = config?.decimal_separator;

  // With known config, do a deterministic transform. Escape the thousand
  // separator for the regex (it can be ".", ",", or " ").
  if (thousand && decimal && thousand !== decimal) {
    const escapedThousand = thousand.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const cleaned = s
      .replace(new RegExp(escapedThousand, "g"), "")
      .replace(decimal, ".");
    const n = parseFloat(cleaned);
    return Number.isNaN(n) ? 0 : n;
  }

  // No config: fall back to heuristic (last separator wins as decimal).
  const commas = (s.match(/,/g) || []).length;
  const dots = (s.match(/\./g) || []).length;
  let cleaned = s;
  if (commas > 0 && dots > 0) {
    cleaned = s.lastIndexOf(",") > s.lastIndexOf(".")
      ? s.replace(/\./g, "").replace(",", ".")
      : s.replace(/,/g, "");
  } else if (commas > 1) {
    cleaned = s.replace(/,/g, "");
  } else if (dots > 1) {
    cleaned = s.replace(/\./g, "");
  } else if (commas === 1) {
    const after = s.split(",")[1];
    cleaned = (after && after.length === 3) ? s.replace(",", "") : s.replace(",", ".");
  } else if (dots === 1) {
    const after = s.split(".")[1];
    cleaned = (after && after.length === 3) ? s.replace(".", "") : s;
  }
  const n = parseFloat(cleaned);
  return Number.isNaN(n) ? 0 : n;
}
